import { type EventFlow } from '@solinkcorp/api-types';
import {
  type EditEventTemplate,
  type FeatureStandard,
} from '@solinkcorp/event-standard/lib/src/types';
import { type IEvent } from 'clients/PlatformApi';

function resolveFieldValue(event: IEvent, fieldPath: string): unknown {
  return fieldPath
    .split('.')
    .reduce((obj, key) => (obj as Record<string, unknown>)?.[key], event as unknown);
}

// Check if event meets the preconditions of workflow template
export function checkPrecondition(
  event: IEvent,
  precondition: EditEventTemplate['precondition'],
  featureStandard: FeatureStandard | undefined,
) {
  const { fieldPath, value, comparison } = precondition;
  const eventValue = resolveFieldValue(event, fieldPath);
  if (eventValue !== undefined) {
    // if the subtype value in defined condition doesn't match the event subtype
    //but it matches the event subtype's `mergeWith` field defined in tabs.yml
    // in event-standard or vice versa, it should be considered as a match
    //because it means the subtypes can be used interchangeably
    if (
      fieldPath === 'subtype' &&
      comparison === '=' &&
      eventValue !== value &&
      typeof eventValue === 'string' &&
      featureStandard
    ) {
      const { tabs } = featureStandard;
      for (const tab of Object.values(tabs)) {
        if (
          tab.eventSubtypes?.[value]?.mergeWith?.includes(eventValue) ||
          tab.eventSubtypes?.[eventValue]?.mergeWith?.includes(value)
        ) {
          return true;
        }
      }
    }

    switch (comparison) {
      case '=':
        return eventValue === value;
      case '<':
        return eventValue && eventValue < value;
      case '>':
        return eventValue && eventValue > value;
      case '<=':
        return eventValue && eventValue <= value;
      case '>=':
        return eventValue && eventValue >= value;
    }
  }
  return false;
}

// Get the first applicable workflow for an event that meets the precondition
export function getFirstApplicableWorkflow(
  event: IEvent,
  workflows: EventFlow[],
  featureStandard: FeatureStandard | undefined,
) {
  for (const workflow of workflows) {
    const { eventFlowMetadata } = workflow;
    if (
      eventFlowMetadata.precondition &&
      checkPrecondition(event, eventFlowMetadata.precondition, featureStandard)
    ) {
      return workflow;
    }
  }
}
